/**
 * Comida Landing Page - Todo en Local
 * Premium food marketplace with internationally competitive UI/UX
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Clock, Star, Truck, X, Utensils, ChevronRight, Zap, ShieldCheck, Timer } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { MarketplaceHeader, MarketplaceFooter, FloatingCartButton, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';
import api from '../../services/api';

// ==================== DEFAULT CUISINE CATEGORIES (always shown) ====================
const IMG_BASE = '/api/v1/uploads/food_categories';
const DEFAULT_CATEGORIES = [
  { name: 'Comida Mexicana', img: `${IMG_BASE}/comida-mexicana.jpg` },
  { name: 'Sushi', img: `${IMG_BASE}/sushi.jpg` },
  { name: 'Pizza', img: `${IMG_BASE}/pizza.jpg` },
  { name: 'Hamburguesas', img: `${IMG_BASE}/hamburguesas.jpg` },
  { name: 'Pollo', img: `${IMG_BASE}/pollo.jpg` },
  { name: 'Tacos', img: `${IMG_BASE}/tacos.jpg` },
  { name: 'Mariscos', img: `${IMG_BASE}/mariscos.jpg` },
  { name: 'Ensaladas', img: `${IMG_BASE}/ensaladas.jpg` },
  { name: 'Desayunos', img: `${IMG_BASE}/desayunos.jpg` },
  { name: 'Postres', img: `${IMG_BASE}/postres.jpg` },
  { name: 'Café', img: `${IMG_BASE}/cafe.jpg` },
  { name: 'Italiana', img: `${IMG_BASE}/italiana.jpg` },
  { name: 'China', img: `${IMG_BASE}/china.jpg` },
  { name: 'Saludable', img: `${IMG_BASE}/saludable.jpg` },
  { name: 'Bebidas', img: `${IMG_BASE}/bebidas.jpg` },
  { name: 'Panadería', img: `${IMG_BASE}/panaderia.jpg` },
];

const ComidaLanding = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [featured, setFeatured] = useState({ restaurants: [], popular_dishes: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openNow, setOpenNow] = useState(false);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
    loadFeatured();
  }, []);

  useEffect(() => {
    loadRestaurants();
  }, [openNow, selectedCuisine, userLocation]);

  const loadFeatured = async () => {
    try {
      const res = await api.get('/comida/featured');
      setFeatured(res.data);
    } catch (e) { console.error(e); }
  };

  const loadRestaurants = async () => {
    try {
      setLoading(true);
      const params = { open_now: openNow, sort_by: userLocation ? 'distance' : 'relevance', limit: 30 };
      if (userLocation) { params.lat = userLocation.lat; params.lng = userLocation.lng; }
      if (selectedCuisine) params.cuisine = selectedCuisine;
      const res = await api.get('/comida/restaurants', { params });
      setRestaurants(res.data.restaurants);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCuisineClick = (name) => {
    setSelectedCuisine(selectedCuisine === name ? null : name);
    window.scrollTo({ top: 600, behavior: 'smooth' });
  };

  const filteredRestaurants = searchQuery
    ? restaurants.filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.cuisine_tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())))
    : restaurants;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Pide Comida a Domicilio | enLocal Comida - Puerto Vallarta</title>
        <meta name="description" content="Pide comida de los mejores restaurantes de Puerto Vallarta. Tacos, sushi, pizza, hamburguesas y más. Entrega rápida a tu puerta con enLocal." />
        <meta property="og:title" content="enLocal Comida | Pide tu comida favorita a domicilio" />
        <meta property="og:description" content="Los mejores restaurantes de Puerto Vallarta en un solo lugar. Entrega rápida con repartidores enLocal." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://todoenlocal.com/comida" />
        <meta property="og:image" content="https://todoenlocal.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="enLocal Comida | Pide tu comida favorita" />
        <meta name="twitter:description" content="Restaurantes de Puerto Vallarta con entrega a domicilio. Tacos, sushi, pizza y más." />
        <link rel="canonical" href="https://todoenlocal.com/comida" />
      </Helmet>
      <MarketplaceHeader />

      {/* ==================== HERO ==================== */}
      <div className="relative overflow-hidden bg-[#1a1a2e]">
        {/* Animated gradient background */}
        <div className="absolute inset-0 opacity-60">
          <div className="absolute top-0 -left-20 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" />
          <div className="absolute top-10 right-0 w-80 h-80 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute -bottom-20 left-1/3 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-14 md:py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-orange-300 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
              <Zap className="w-4 h-4" /> Entrega en minutos
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-4">
              Tu comida favorita,<br />
              <span className="bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent">a tu puerta</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl mb-8">
              Los mejores restaurantes de Puerto Vallarta en un solo lugar
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar restaurante, platillo o cocina..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-12 py-5 rounded-xl text-gray-900 text-base md:text-lg bg-white shadow-2xl shadow-black/20 focus:ring-4 focus:ring-orange-500/30 focus:outline-none transition-shadow"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-100 p-1.5 rounded-full hover:bg-gray-200 transition">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== CUISINE CATEGORIES GRID ==================== */}
      <div className="max-w-6xl mx-auto px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-6 md:p-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl md:text-2xl font-extrabold text-gray-900">Explora por tipo de cocina</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {DEFAULT_CATEGORIES.map((cat) => {
              const isActive = selectedCuisine === cat.name;
              return (
                <button
                  key={cat.name}
                  onClick={() => handleCuisineClick(cat.name)}
                  className={`group relative overflow-hidden rounded-2xl transition-all duration-200 ${
                    isActive
                      ? 'ring-3 ring-orange-500 shadow-lg scale-[1.03]'
                      : 'hover:shadow-lg hover:-translate-y-1'
                  }`}
                >
                  <div className="aspect-square relative">
                    <img src={cat.img} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className={`absolute inset-0 transition-colors duration-200 ${isActive ? 'bg-orange-600/50' : 'bg-black/35 group-hover:bg-black/45'}`} />
                    <div className="absolute inset-0 flex items-end p-2">
                      <span className="text-white text-xs md:text-sm font-bold leading-tight drop-shadow-lg">{cat.name}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-10">

        {/* ==================== HOW IT WORKS ==================== */}
        {!searchQuery && !selectedCuisine && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: <Search className="w-6 h-6" />, title: 'Elige', desc: 'Explora restaurantes y platillos cerca de ti', color: 'bg-orange-100 text-orange-600' },
              { icon: <Timer className="w-6 h-6" />, title: 'Pedimos', desc: 'Tu restaurante prepara tu orden al momento', color: 'bg-red-100 text-red-600' },
              { icon: <Truck className="w-6 h-6" />, title: 'Entregamos', desc: 'Un repartidor enLocal lleva tu comida a casa', color: 'bg-green-100 text-green-600' },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                <div className={`w-12 h-12 rounded-xl ${step.color} flex items-center justify-center flex-shrink-0`}>
                  {step.icon}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{step.title}</h3>
                  <p className="text-sm text-gray-500">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ==================== PROMO BANNER ==================== */}
        {!searchQuery && !selectedCuisine && (
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 p-8 md:p-10 text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20" />
            <div className="absolute bottom-0 left-1/2 w-40 h-40 bg-white/5 rounded-full -mb-10" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm font-medium mb-3">
                <Zap className="w-4 h-4" /> Nuevo
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold mb-2">Envío por enLocal</h3>
              <p className="text-white/80 max-w-md">Ahora puedes recibir tu comida favorita con nuestra flotilla de repartidores independientes. Rápido, seguro y confiable.</p>
            </div>
          </div>
        )}

        {/* ==================== FILTERS BAR ==================== */}
        <div className="flex items-center gap-3 flex-wrap">
          {selectedCuisine && (
            <div className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium">
              {selectedCuisine}
              <button onClick={() => setSelectedCuisine(null)} className="ml-1 hover:bg-white/20 rounded-full p-0.5">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <button
            onClick={() => setOpenNow(!openNow)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${openNow ? 'bg-green-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-green-400'}`}
          >
            <div className={`w-2 h-2 rounded-full ${openNow ? 'bg-white' : 'bg-green-500'} animate-pulse`} />
            Abierto ahora
          </button>
          {userLocation && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500 bg-white px-3 py-2 rounded-full border border-gray-200">
              <MapPin className="w-3.5 h-3.5 text-orange-500" />
              Cerca de ti
            </div>
          )}
        </div>

        {/* ==================== FEATURED RESTAURANTS ==================== */}
        {!searchQuery && !selectedCuisine && featured.restaurants.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-2xl font-extrabold text-gray-900">Restaurantes destacados</h2>
                <p className="text-sm text-gray-500 mt-0.5">Los mejores valorados por nuestros clientes</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.restaurants.slice(0, 6).map((r) => (
                <RestaurantCard key={r.business_id} restaurant={r} onClick={() => navigate(`/comida/restaurante/${r.business_id}`)} featured />
              ))}
            </div>
          </div>
        )}

        {/* ==================== POPULAR DISHES ==================== */}
        {!searchQuery && !selectedCuisine && featured.popular_dishes?.length > 0 && (
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-5">Platillos populares</h2>
            <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4">
              {featured.popular_dishes.map((dish) => (
                <Link
                  key={dish.id}
                  to={`/comida/restaurante/${dish.business_id}`}
                  className="flex-shrink-0 w-64 md:w-72 bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className="relative">
                    {dish.image_url ? (
                      <img src={dish.image_url} alt={dish.name} className="w-full h-44 md:h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-44 md:h-48 bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center">
                        <Utensils className="w-12 h-12 text-orange-300" />
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent h-20" />
                    <span className="absolute bottom-3 right-3 bg-white text-orange-600 font-extrabold text-sm px-3 py-1 rounded-lg shadow">
                      ${(dish.is_combo ? dish.combo_price : dish.pricing?.retail_price || 0).toFixed(0)}
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="font-bold text-gray-900 text-base truncate group-hover:text-orange-600 transition-colors">{dish.name}</p>
                    <p className="text-sm text-gray-500 truncate mt-0.5">{dish.business_name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ==================== RESTAURANT LIST ==================== */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-extrabold text-gray-900">
              {selectedCuisine || 'Todos los restaurantes'}
            </h2>
            <span className="text-sm text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{filteredRestaurants.length}</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filteredRestaurants.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Utensils className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-gray-900 text-xl font-bold mb-2">
                {selectedCuisine ? `Sin restaurantes de ${selectedCuisine}` : 'No se encontraron restaurantes'}
              </p>
              <p className="text-gray-400 max-w-sm mx-auto">
                {selectedCuisine
                  ? 'Pronto tendremos restaurantes de este tipo. Explora otras categorías mientras tanto.'
                  : 'Intenta con otra búsqueda o categoría'}
              </p>
              {selectedCuisine && (
                <button onClick={() => setSelectedCuisine(null)} className="mt-5 px-6 py-2.5 bg-orange-600 text-white rounded-full font-medium hover:bg-orange-700 transition">
                  Ver todos los restaurantes
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredRestaurants.map((r) => (
                <RestaurantCard key={r.business_id} restaurant={r} onClick={() => navigate(`/comida/restaurante/${r.business_id}`)} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pb-24" />
      <MarketplaceFooter />
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};


// ==================== RESTAURANT CARD ====================

const RestaurantCard = ({ restaurant: r, onClick, featured }) => (
  <button onClick={onClick} className="text-left bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-gray-200 transition-all duration-300 group w-full hover:-translate-y-1">
    <div className="relative">
      {r.cover_image_url || r.logo_url ? (
        <img src={r.cover_image_url || r.logo_url} alt={r.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <div className="w-full h-48 bg-gradient-to-br from-orange-400 via-red-500 to-pink-500 flex items-center justify-center">
          <Utensils className="w-14 h-14 text-white/30" />
        </div>
      )}
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

      {!r.is_open && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[1px]">
          <span className="bg-white/90 text-gray-900 px-5 py-2 rounded-full text-sm font-bold shadow-lg">Cerrado ahora</span>
        </div>
      )}

      {/* Badges */}
      <div className="absolute top-3 left-3 flex gap-2">
        {r.verification_badge && (
          <span className="bg-blue-600/90 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 shadow-lg">
            <ShieldCheck className="w-3.5 h-3.5" /> Verificado
          </span>
        )}
        {featured && (
          <span className="bg-orange-500/90 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-xs font-semibold shadow-lg">
            Destacado
          </span>
        )}
      </div>

      {/* Prep time badge */}
      <div className="absolute bottom-3 right-3">
        <span className="bg-white/95 backdrop-blur-sm text-gray-900 px-3 py-1.5 rounded-xl text-xs font-bold shadow-lg flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-orange-500" /> {r.prep_time} min
        </span>
      </div>
    </div>

    <div className="p-4">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-extrabold text-gray-900 text-lg group-hover:text-orange-600 transition-colors truncate">{r.name}</h3>
        {r.rating && (
          <span className="flex items-center gap-1 bg-green-50 text-green-700 px-2 py-0.5 rounded-lg text-sm font-bold flex-shrink-0">
            <Star className="w-3.5 h-3.5 fill-current" /> {r.rating}
          </span>
        )}
      </div>

      {r.cuisine_tags?.length > 0 && (
        <p className="text-sm text-gray-500 mt-1 truncate">{r.cuisine_tags.join(' \u00b7 ')}</p>
      )}

      <div className="flex items-center gap-3 mt-3 flex-wrap">
        {r.delivery_enabled && (
          <span className="flex items-center gap-1.5 text-xs bg-gray-50 text-gray-600 px-2.5 py-1.5 rounded-lg font-medium">
            <Truck className="w-3.5 h-3.5 text-green-600" />
            {r.delivery_fee ? `Envío $${r.delivery_fee}` : 'Envío gratis'}
          </span>
        )}
        {r.pickup_enabled && (
          <span className="text-xs bg-gray-50 text-gray-600 px-2.5 py-1.5 rounded-lg font-medium">
            Recoger
          </span>
        )}
        {r.distance_km != null && (
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <MapPin className="w-3 h-3" /> {r.distance_km} km
          </span>
        )}
      </div>
    </div>
  </button>
);


// ==================== SKELETON CARD ====================

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
    <div className="w-full h-48 bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="flex justify-between">
        <div className="h-5 bg-gray-200 rounded-lg w-3/5" />
        <div className="h-5 bg-gray-200 rounded-lg w-12" />
      </div>
      <div className="h-4 bg-gray-100 rounded w-4/5" />
      <div className="flex gap-2">
        <div className="h-7 bg-gray-100 rounded-lg w-24" />
        <div className="h-7 bg-gray-100 rounded-lg w-16" />
      </div>
    </div>
  </div>
);


export default ComidaLanding;
